import React, { useState, useEffect, useRef, useMemo } from "react";
import clockIcon from "../../assets/clock.svg";
import "./VideoTimeline.css";
import { ReactComponent as StarIcon } from "../../assets/starIconPurple.svg";
import useIsMobile from "../../hooks/useIsMobile";
import Hls from "hls.js";
import TimelinePreview from "../hover-preview/TimelinePreview.jsx";
import { createFrameSource } from "../hover-preview/frameSources.js";
import { attachTouchScrub } from "../hover-preview/touchScrub.js";

const formatTime = (seconds) => {
  if (!seconds) return "00:00";

  const totalMinutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  const pad = (num) => String(num).padStart(2, "0");

  return `${pad(totalMinutes)}:${pad(remainingSeconds)}`;
};

const VideoTimeline = ({
  newMarkerInfo,
  version,
  isViewer,
  setNewMarkerInfo,
  currentTime = 0,
  totalTime = 0,
  progress = 0,
  videoControls,
  newCommentEnd,
  setNewCommentEnd = () => {},
  comments = [],
  onCommentMarkerClick,
  currentHoveredCommentId,
  setCurrentHoveredCommentId,
  playbackStarted,
  isPlaying,
  setPlaybackStarted,
  selectedTag,
  tagsDefaultColors,
}) => {
  const [showRemainingTime, setShowRemainingTime] = useState(false);
  const [showTimeModeModal, setShowTimeModeModal] = useState(false);
  const [isTimelineHovered, setIsTimelineHovered] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [markerArrowsVisible, setMarkerArrowsVisible] = useState(false);
  const [arrowHovered, setArrowHovered] = useState(null);
  const [hoverTime, setHoverTime] = useState(0);
  const [timelineMarkers, setTimelineMarkers] = useState([]);
  const [timelineElements, setTimelineElements] = useState([]);
  const [currentEditedMarker, setCurrentEditedMarker] = useState(null);
  const [isLeftMarkerEdgeMoving, setIsLeftMarkerEdgeMoving] = useState(false);
  const [isRightMarkerEdgeMoving, setIsRightMarkerEdgeMoving] = useState(false);
  const isMobile = useIsMobile();

  const timelineMarkersRowRef = useRef();

  const frameSource = useMemo(
    () => createFrameSource(version, { Hls, duration: totalTime }),
    [version?.id, version?.spriteVtt, version?.hlsUrl, totalTime > 0] // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => () => frameSource?.destroy(), [frameSource]);

  useEffect(() => {
    const markers = generateCommentMarkers();
    const processedMarkers = processMarkers(markers);
    const elements = generateTimelineElements(processedMarkers);

    setTimelineMarkers(processedMarkers);
    setTimelineElements(elements);
  }, [selectedTag, comments, tagsDefaultColors]);

  useEffect(() => {
    const updateMousePosition = (event) => {
      processMouseMovement(event);
    };
    const mouseUpProcess = (event) => {
      processMouseUp(event);
    };

    window.addEventListener("mouseup", mouseUpProcess);
    window.addEventListener("mousemove", updateMousePosition);

    return () => {
      window.removeEventListener("mousemove", updateMousePosition);
      window.removeEventListener("mouseup", mouseUpProcess);
    };
  }, [isLeftMarkerEdgeMoving, isRightMarkerEdgeMoving]);

  useEffect(() => {
    if (!isPlaying && playbackStarted) {
      const timecode = videoControls?.getCurrentTime() || 0;
      const basePosition = Math.min((timecode / totalTime) * 100, 100);
      const newCommentMarker = {
        id: "noop",
        position: basePosition,
        timecode,
        color: "white",
        isNewComment: true,
      };

      setNewMarkerInfo(newCommentMarker);
    } else {
      if (newMarkerInfo) {
        setNewCommentEnd({
          start: newMarkerInfo.timecode,
          end: null,
        });
      }
      setNewMarkerInfo(null);
    }
  }, [isPlaying, playbackStarted]);

  const processMouseUp = (e) => {
    if (isLeftMarkerEdgeMoving || isRightMarkerEdgeMoving) {
      endDragging(e);
    }
  };

  const processMouseMovement = (event) => {
    if (!isLeftMarkerEdgeMoving && !isRightMarkerEdgeMoving) return;
    event.preventDefault();
    event.stopPropagation();

    if (isLeftMarkerEdgeMoving) {
      const rect = timelineMarkersRowRef.current?.getBoundingClientRect();
      let newPos = ((event.screenX - 50) / rect.width) * 100;
      newPos = Math.max(0, Math.min(newPos, 100));

      if (newMarkerInfo.positionEnd) {
        newPos = Math.min(newPos, newMarkerInfo.positionEnd);
      }

      if (!newMarkerInfo.positionEnd) {
        newPos = Math.min(newPos, newMarkerInfo.position);
      }
      const newTimecode = (newPos / 100) * totalTime;

      videoControls?.seek(newTimecode);

      let updatedNewCommentMarker = {
        ...newMarkerInfo,
        position: newPos,
        timecode: newTimecode,
      };

      if (!updatedNewCommentMarker.positionEnd) {
        updatedNewCommentMarker = {
          ...updatedNewCommentMarker,
          positionEnd: newMarkerInfo.position,
          timecodeEnd: newMarkerInfo.timecode,
        };
      }

      let newTimeRange = {
        start: updatedNewCommentMarker.timecode,
        end:
          updatedNewCommentMarker.timecodeEnd -
            updatedNewCommentMarker.timecode <
          0.01
            ? null
            : updatedNewCommentMarker.timecodeEnd,
      };

      // if (newMarkerInfo.timecodeEnd) {
      //   const diff = newTimecode - newMarkerInfo.timecode;
      //   const posDiff = newPos - newMarkerInfo.position;
      //   newTimeRange = {
      //     ...newTimeRange,
      //     end: diff + updatedNewCommentMarker.timecodeEnd,
      //   };
      //   updatedNewCommentMarker = {
      //     ...updatedNewCommentMarker,
      //     timecodeEnd: diff + updatedNewCommentMarker.timecodeEnd,
      //     positionEnd: posDiff + updatedNewCommentMarker.positionEnd,
      //   };
      // }
      setNewCommentEnd(newTimeRange);
      setNewMarkerInfo(updatedNewCommentMarker);
    } else if (isRightMarkerEdgeMoving) {
      const rect = timelineMarkersRowRef.current?.getBoundingClientRect();
      let newPosEnd = ((event.screenX - 70) / rect.width) * 100;

      newPosEnd = Math.max(newMarkerInfo.position, Math.min(newPosEnd, 100));
      const newTimecodeEnd = (newPosEnd / 100) * totalTime;

      videoControls?.seek(newTimecodeEnd);

      const updatedNewCommentMarker = {
        ...newMarkerInfo,
        positionEnd:
          newTimecodeEnd - newMarkerInfo.timecode < 0.01 ? null : newPosEnd,
        timecodeEnd:
          newTimecodeEnd - newMarkerInfo.timecode < 0.01
            ? null
            : newTimecodeEnd,
      };

      const newTimeRange = {
        start: updatedNewCommentMarker.timecode,
        end: updatedNewCommentMarker.timecodeEnd,
      };

      setNewCommentEnd(newTimeRange);
      setNewMarkerInfo(updatedNewCommentMarker);
    }
  };

  useEffect(() => {
    generateTimelineContent();
  }, [comments, totalTime]);

  const generateTimelineContent = () => {
    const myDiv = document.getElementById("timeline-markers-row");
    const calculatedWidth = myDiv.offsetWidth;
    const markers = generateCommentMarkers();
    const processedMarkers = processMarkers(markers);
    const elements = generateTimelineElements(processedMarkers);

    setTimelineMarkers(processedMarkers);
    setTimelineElements(elements);
  };

  const getAverage = (numbers) => {
    let sum = 0;
    for (let i = 0; i < numbers.length; i += 1) {
      sum += numbers[i];
    }
    return sum / numbers.length;
  };

  const processMarkers = (markersArray) => {
    const sortedMarkers = [...markersArray].sort((a, b) =>
      a.position > b.position ? 1 : -1
    );

    let markerGroups = [];
    let currentMarkerGroup = [];

    const tagFilterId =
      !selectedTag || selectedTag.name === "Все" ? null : selectedTag.id;

    for (let i = 0; i < sortedMarkers.length; i++) {
      if (i === 0) {
        currentMarkerGroup = [0];
      }
      if (i === sortedMarkers.length - 1) {
        markerGroups.push(currentMarkerGroup);
        break;
      }
      let marker = sortedMarkers[i];
      let nextMarker = sortedMarkers[i + 1];

      let offset = nextMarker.position - marker.position;

      if (
        offset < 1 &&
        (!tagFilterId ||
          (nextMarker.tags &&
            marker.tags.includes(tagFilterId) &&
            nextMarker.tags.includes(tagFilterId)))
      ) {
        currentMarkerGroup.push(i + 1);
      } else {
        markerGroups.push(currentMarkerGroup);
        currentMarkerGroup = [i + 1];
      }
    }

    let processedMarkers = [];

    for (let k = 0; k < markerGroups.length; k++) {
      const currentGroup = markerGroups[k];

      if (currentGroup.length > 1) {
        const firstGroupComment = sortedMarkers[currentGroup[0]];
        const currentGroupCommentIds = currentGroup.map((commentIndex) => {
          return sortedMarkers[commentIndex].id;
        });
        const averagePosition = getAverage(
          currentGroup.map((ind) => sortedMarkers[ind].position)
        );
        let groupedMarkersObject = {
          id: firstGroupComment.id,
          position: averagePosition,
          timecode: firstGroupComment.timecode,
          color: firstGroupComment.color || null,
          isGroup: true,
          groupCount: currentGroup.length,
          groupCommentIds: currentGroupCommentIds,
        };
        processedMarkers.push(groupedMarkersObject);
      } else {
        const singleMarkerToAdd = sortedMarkers[currentGroup[0]];
        processedMarkers.push(singleMarkerToAdd);
      }
    }
    return processedMarkers;
  };

  const handleProgressClick = (e) => {
    if (!videoControls) return;

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickPosition = e.clientX - rect.left;
    const percentage = Math.max(
      0,
      Math.min((clickPosition / rect.width) * 100, 100)
    );
    const seekTime = (percentage / 100) * totalTime;

    videoControls.seek(seekTime);
    if (isPlaying) return;

    setNewMarkerInfo({
      id: "noop",
      position: percentage,
      timecode: seekTime,
      color: "white",
      isNewComment: true,
      positionEnd: null,
      timecodeEnd: null,
    });
    setNewCommentEnd({
      start: seekTime,
      end: null,
    });
    setPlaybackStarted(true);
  };

  const handleTimelineMouseMove = (e) => {
    const timelineContainer = e.currentTarget.closest(".timeline-container");
    if (!timelineContainer) return;

    const containerRect = timelineContainer.getBoundingClientRect();
    const containerWidth = containerRect.width - 32;
    const containerLeft = containerRect.left + 16;
    const mousePosition = e.clientX - containerLeft;

    const timePercentage = (mousePosition / containerWidth) * 100;
    const timeAtPosition = (timePercentage / 100) * totalTime;

    const positionPercentage =
      ((e.clientX - containerRect.left) / containerRect.width) * 100;

    setHoverPosition(positionPercentage);
    setHoverTime(timeAtPosition);
  };

  const handleTimelineMouseEnter = () => {
    if (isLeftMarkerEdgeMoving || isRightMarkerEdgeMoving) return;
    setIsTimelineHovered(true);
  };

  const handleTimelineMouseLeave = () => {
    setIsTimelineHovered(false);
  };

  const handleTimeDisplayClick = () => {
    setShowTimeModeModal(true);
  };

  const handleSelectTimeMode = (mode) => {
    setShowRemainingTime(mode === "remaining");
    setShowTimeModeModal(false);
  };

  const formatRemainingTime = (current, total) => {
    const remaining = total - current;
    return `-${formatTime(remaining)}`;
  };

  const generateTimelineElements = (processedMarkers) => {
    let elements = [];

    const rect = timelineMarkersRowRef.current?.getBoundingClientRect();
    const rowWidth = rect.width;
    const chunkWidth = rowWidth / 96;

    const FourDots = () => (
      <div
        className="four-dots"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          width: isMobile ? "10%" : "3.5%",
        }}
      >
        <div className="timeline-dot" />
        <div className="timeline-dot" />
        <div className="timeline-dot" />
        <div className="timeline-dot" />
      </div>
    );

    const ThreeDots = () => (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          width: "3.5%",
        }}
      >
        <div className="timeline-dot" style={{ backgroundColor: "red" }} />
        <div className="timeline-dot" style={{ backgroundColor: "red" }} />
        <div className="timeline-dot" style={{ backgroundColor: "red" }} />
        <div className="timeline-dot" style={{ backgroundColor: "red" }} />
      </div>
    );

    const Timestamp = ({ time, isHidden }) => (
      <div className="timeline-marker">
        <span
          className="marker-time"
          style={{
            color: isHidden ? "transparent" : "rgba(255, 255, 255, 0.6)",
          }}
        >
          {time}
        </span>
      </div>
    );

    const timestampsAmount = isMobile ? 5 : 12;

    for (let i = 0; i < timestampsAmount; i++) {
      let time = "00:00";
      if (i === 0) {
        time = "00:00";
      } else if (i == timestampsAmount - 1) {
        time = formatTime(totalTime);
      } else {
        time = formatTime((i / (timestampsAmount - 1)) * totalTime);
      }

      const currentLeftPos = 95 * i;

      let isHidden = false;
      for (let a = 0; a < processedMarkers.length; a++) {
        const markerRelativePos = processedMarkers[a].position;
        const timestampRelativePos = (currentLeftPos / rowWidth) * 100;
        if (
          Math.abs(
            (currentLeftPos / rowWidth) * 100 - processedMarkers[a].position
          ) < 5
        ) {
          isHidden = true;
          break;
        }
      }

      isHidden = false;

      if (isHidden) {
        elements.push(() => <ThreeDots />);
      } else {
        elements.push(() => <Timestamp time={time} />);
      }

      elements.push(() => <FourDots />);
    }

    elements = elements.slice(0, -1);
    return elements;
  };

  const generateCommentMarkers = () => {
    const tagsColors = JSON.parse(localStorage.getItem("tags_colors", {}));

    const result = comments.map((comment) => {
      const savedTagColor =
        tagsColors && comment.tags.length && tagsColors[comment.tags[0].id];
      if (!comment.timecode.includes("-")) {
        const timeInSeconds = +comment.timecode;
        const basePosition = Math.min((timeInSeconds / totalTime) * 100, 100);
        const commentRes = {
          id: comment.id,
          position: basePosition,
          timecode: comment.timecode,
          status: comment.status || null,
          color:
            savedTagColor ||
            (comment.tags.length && tagsDefaultColors[comment.tags[0].id]) ||
            (comment.tags.length ? comment.tags[0].color : null),
          tags:
            (comment.tags &&
              comment.tags.length &&
              comment.tags.map((tag) => tag.id)) ||
            [],
        };
        return commentRes;
      }
      const [timeRangeStart, timeRangeEnd] = comment.timecode.split(" - ");
      const timeInSeconds = +timeRangeStart;
      const timeInSecondsEnd = +timeRangeEnd;
      const basePosition = Math.min((timeInSeconds / totalTime) * 100, 100);
      const basePositionEnd = Math.min(
        (timeInSecondsEnd / totalTime) * 100,
        100
      );

      const commentRes = {
        id: comment.id,
        position: basePosition,
        positionEnd: basePositionEnd,
        timecode: timeRangeStart,
        status: comment.status || null,
        timecodeEnd: timeRangeEnd,
        color:
          savedTagColor ||
          (comment.tags.length && tagsDefaultColors[comment.tags[0].id]) ||
          (comment.tags.length ? comment.tags[0].color : null),
        tags:
          (comment.tags &&
            comment.tags.length &&
            comment.tags.map((tag) => tag.id)) ||
          [],
      };

      return commentRes;
    });

    return result;
  };

  const handleSeekTimecode = (timecode, marker = null) => {
    setPlaybackStarted(true);
    if (timecode.includes("-")) {
      const [timecodeStart, timecodeEnd] = timecode.split(" - ");
      videoControls.seek(+timecodeStart);
    } else {
      videoControls.seek(timecode);
    }

    if (!marker) return;

    const updatedNewMarker = {
      ...newMarkerInfo,
      position: marker.position,
      timecode: marker.timecode,
      positionEnd: null,
      timecodeEnd: null,
    };
    setNewMarkerInfo(updatedNewMarker);
    setNewCommentEnd({
      start: updatedNewMarker.timecode,
      end: null,
    });
    setPlaybackStarted(true);
  };

  const startDragging = (event, isStart = true) => {
    event.preventDefault();
    if (isStart) {
      setIsLeftMarkerEdgeMoving(true);
      setIsRightMarkerEdgeMoving(false);
    } else {
      setIsRightMarkerEdgeMoving(true);
      setIsLeftMarkerEdgeMoving(false);
    }
  };

  const endDragging = (event) => {
    setIsLeftMarkerEdgeMoving(false);
    setIsRightMarkerEdgeMoving(false);
  };

  const onClickSingleMarker = (marker) => {
    // setCurrentEditedMarker(marker);
  };

  const getRightCaretLeftPos = () => {
    const positionPercent =
      (newMarkerInfo.positionEnd - newMarkerInfo.position) / 100;
    const timelineWidth =
      timelineMarkersRowRef.current?.getBoundingClientRect().width;
    const res = 10 + timelineWidth * positionPercent;
    return res;
  };

  return (
    <div className="video-timeline">
      <div>
        <div
          ref={timelineMarkersRowRef}
          id="timeline-markers-row"
          className="timeline-markers"
        >
          {timelineElements.map((Element) => (
            <Element />
          ))}

          {timelineMarkers.map((marker) =>
            marker.isGroup ? (
              <div
                onClick={() => {
                  handleSeekTimecode(marker.timecode, marker);
                }}
                onMouseEnter={() => {
                  // if (onCommentMarkerClick) {
                  //   onCommentMarkerClick(marker.id);
                  // }
                  setCurrentHoveredCommentId(marker.id);
                }}
                onMouseLeave={() => {
                  setCurrentHoveredCommentId(null);
                }}
                key={`comment-${marker.id}`}
                className="comment-marker-group"
                style={{
                  left: `${marker.position}%`,
                  backgroundColor: marker.groupCommentIds.includes(
                    currentHoveredCommentId
                  )
                    ? "white"
                    : marker.color || "#9a6aff",
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 500, color: "black" }}>
                  {marker.groupCount}
                </span>
              </div>
            ) : (
              <>
                <div
                  onClick={() => {
                    handleSeekTimecode(marker.timecode, marker);
                  }}
                  onMouseEnter={() => {
                    // if (onCommentMarkerClick) {
                    //   onCommentMarkerClick(marker.id);
                    // }
                    setCurrentHoveredCommentId(marker.id);
                  }}
                  onMouseLeave={() => {
                    setCurrentHoveredCommentId(null);
                  }}
                  key={`comment-${marker.id}`}
                  className={`comment-marker${
                    marker.status ? ` comment-marker--${marker.status}` : ""
                  }`}
                  style={{ left: `${marker.position}%` }}
                >
                  <StarIcon
                    style={{
                      transform: "scale(2.8)",
                      fill:
                        marker.status === "failed"
                          ? "#e5484d"
                          : marker.status === "sending"
                            ? "#9a6aff"
                            : currentHoveredCommentId === marker.id ||
                                (currentEditedMarker &&
                                  currentEditedMarker.id === marker.id)
                              ? "white"
                              : marker.color
                                ? marker.color
                                : "#9a6aff",
                      opacity:
                        !selectedTag ||
                        selectedTag.name === "Все" ||
                        (marker.tags && marker.tags.includes(selectedTag.id))
                          ? 1
                          : 0.15,
                    }}
                    width={24}
                    height={24}
                  />
                  {marker.isNewComment && (
                    <>
                      <div
                        onMouseDown={(e) => startDragging(e, true)}
                        className="marker-arrow"
                        style={{
                          position: "absolute",
                          userSelect: "none",
                          left: -10,
                          top: -7,
                          color: "white",
                          fontWeight: "bold",
                          fontSize: 16,
                        }}
                      >
                        {"<"}
                      </div>
                      <div
                        onMouseDown={(e) => startDragging(e, false)}
                        className="marker-arrow"
                        style={{
                          position: "absolute",
                          userSelect: "none",
                          left: 10,
                          top: -7,
                          fontSize: 16,
                          fontWeight: "bold",
                          color: "white",
                        }}
                      >
                        {">"}
                      </div>
                    </>
                  )}
                </div>

                {marker.timecodeEnd && (
                  <div
                    style={{
                      height: 2,
                      // left: `${
                      //   (marker.positionEnd - marker.position) / 2 + 2
                      // }%`,
                      left: `${marker.position}%`,
                      width: `${marker.positionEnd - marker.position}%`,
                      top: -11,
                      backgroundColor:
                        currentHoveredCommentId === marker.id
                          ? "white"
                          : marker.color
                            ? marker.color
                            : "#9a6aff",
                      position: "absolute",
                    }}
                  ></div>
                )}
              </>
            )
          )}
          {newMarkerInfo && !isViewer && (
            <>
              <div
                onClick={() => {
                  onClickSingleMarker(newMarkerInfo);
                }}
                onMouseEnter={() => {
                  // if (onCommentMarkerClick) {
                  //   onCommentMarkerClick(newMarkerInfo.id);
                  // }
                  setCurrentHoveredCommentId(newMarkerInfo.id);
                }}
                onMouseLeave={() => {
                  setCurrentHoveredCommentId(null);
                }}
                key={`comment-${newMarkerInfo.id}`}
                className="comment-marker"
                style={{ left: `${newMarkerInfo.position}%`, bottom: 48 }}
              >
                <StarIcon
                  onMouseEnter={() => setMarkerArrowsVisible(true)}
                  onMouseLeave={() => setMarkerArrowsVisible(false)}
                  style={{
                    transform: "scale(2.8)",
                    fill: "white",
                    // currentHoveredCommentId === newMarkerInfo.id ||
                    // (currentEditedMarker &&
                    //   currentEditedMarker.id === newMarkerInfo.id)
                    //   ? "white"
                    //   : newMarkerInfo.color
                    //   ? newMarkerInfo.color
                    //   : "#9a6aff",
                  }}
                  width={24}
                  height={24}
                />
                <>
                  <div
                    onMouseEnter={() => {
                      setMarkerArrowsVisible(true);
                      setArrowHovered("left");
                    }}
                    onMouseLeave={() => {
                      setMarkerArrowsVisible(false);
                      setArrowHovered(null);
                    }}
                    onMouseDown={(e) => startDragging(e, true)}
                    className="marker-arrow"
                    style={{
                      position: "absolute",
                      userSelect: "none",
                      left: -10,
                      top: -7,
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 16,
                      opacity:
                        arrowHovered === "left" || isLeftMarkerEdgeMoving
                          ? 1
                          : markerArrowsVisible ||
                              isLeftMarkerEdgeMoving ||
                              isRightMarkerEdgeMoving
                            ? 0.3
                            : 0,
                    }}
                  >
                    {"<"}
                  </div>
                  <div
                    onMouseDown={(e) => startDragging(e, false)}
                    onMouseEnter={() => {
                      setMarkerArrowsVisible(true);
                      setArrowHovered("right");
                    }}
                    onMouseLeave={() => {
                      setMarkerArrowsVisible(false);
                      setArrowHovered(null);
                    }}
                    className="marker-arrow"
                    style={{
                      position: "absolute",
                      userSelect: "none",
                      left: !newMarkerInfo.timecodeEnd
                        ? 10
                        : getRightCaretLeftPos(),
                      top: -7,
                      fontSize: 16,
                      fontWeight: "bold",
                      color: "white",
                      opacity:
                        arrowHovered === "right" || isRightMarkerEdgeMoving
                          ? 1
                          : markerArrowsVisible ||
                              isLeftMarkerEdgeMoving ||
                              isRightMarkerEdgeMoving
                            ? 0.3
                            : 0,
                    }}
                  >
                    {">"}
                  </div>
                </>
              </div>

              {newMarkerInfo.timecodeEnd && (
                <div
                  onMouseEnter={() => setMarkerArrowsVisible(true)}
                  onMouseLeave={() => setMarkerArrowsVisible(false)}
                  style={{
                    height: 2,
                    top: -29,
                    zIndex: 2,
                    // left: `${
                    //   (marker.positionEnd - marker.position) / 2 + 2
                    // }%`,
                    left: `${newMarkerInfo.position}%`,
                    bottom: 30,
                    width: `${
                      newMarkerInfo.positionEnd - newMarkerInfo.position
                    }%`,
                    backgroundColor: "white",
                    // currentHoveredCommentId === newMarkerInfo.id
                    //   ? "white"
                    //   : newMarkerInfo.color
                    //   ? newMarkerInfo.color
                    //   : "#9a6aff",
                    position: "absolute",
                  }}
                ></div>
              )}
            </>
          )}
        </div>

        <div className="timeline-container">
          <div
            className="progress-bar"
            onClick={handleProgressClick}
            onMouseMove={handleTimelineMouseMove}
            onMouseEnter={handleTimelineMouseEnter}
            onMouseLeave={handleTimelineMouseLeave}
          >
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>

          {/* Timeline hover threshold line and popup */}
          {/* {isTimelineHovered && (
            <>
              <div
                className="timeline-threshold-line"
                style={{ left: `${hoverPosition}%` }}
              />
              <div
                className="timeline-hover-popup"
                style={{ left: `${hoverPosition}%` }}
              >
                {formatTime(hoverTime)}
              </div>
            </>
          )} */}
          {isTimelineHovered && (
            <>
              <div
                className="timeline-threshold-line"
                style={{ left: `${hoverPosition}%` }}
              />
              <TimelinePreview
                visible
                time={hoverTime}
                leftPercent={hoverPosition}
                frameSource={frameSource}
                bottom={46} /* там же, где стояла .timeline-hover-popup */
              />
            </>
          )}

          <div className="time-display">
            <span className="current-time">{formatTime(currentTime)}</span>
            <span
              className="total-time"
              onClick={handleTimeDisplayClick}
              style={{ cursor: "pointer" }}
            >
              {showRemainingTime
                ? formatRemainingTime(currentTime, totalTime)
                : formatTime(totalTime)}
            </span>
            <div
              onClick={handleTimeDisplayClick}
              style={{
                position: "absolute",
                right: -15,
                top: -1,
                height: 8,
                cursor: "pointer",
              }}
            >
              <img
                src={clockIcon}
                alt="clock"
                style={{ width: 8, height: 8 }}
              />
            </div>

            {showTimeModeModal && (
              <div
                className="time-mode-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className={
                    "time-mode-row" + (showRemainingTime ? "" : " inactive")
                  }
                  onClick={() => handleSelectTimeMode("remaining")}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={clockIcon}
                      alt="clock"
                      style={{ width: 10, height: 10 }}
                    />
                    <span
                      style={{ color: "#c1c4cb", fontSize: 14, marginLeft: 5 }}
                    >
                      осталось
                    </span>
                  </div>
                  <span
                    style={{ marginLeft: 12, color: "#fff", fontWeight: 500 }}
                  >
                    {formatRemainingTime(currentTime, totalTime)}
                  </span>
                </div>
                <div
                  className={
                    "time-mode-row" + (!showRemainingTime ? "" : " inactive")
                  }
                  onClick={() => handleSelectTimeMode("total")}
                  style={{ marginLeft: 8 }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={clockIcon}
                      alt="clock"
                      style={{ width: 10, height: 10 }}
                    />
                    <span
                      style={{ color: "#c1c4cb", fontSize: 14, marginLeft: 5 }}
                    >
                      всего
                    </span>
                  </div>
                  <span
                    style={{ marginLeft: 12, color: "#fff", fontWeight: 500 }}
                  >
                    {formatTime(totalTime)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showTimeModeModal && (
        <div
          className="time-mode-modal-overlay"
          onClick={() => setShowTimeModeModal(false)}
        />
      )}
    </div>
  );
};

export default VideoTimeline;
